import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { studentActionQueueApi } from '../services/apiV2';

type Priority = 'urgent' | 'today' | 'monitor';
type WorkflowStatus = 'open' | 'in_progress' | 'snoozed' | 'resolved' | 'dismissed';

interface ActionReason {
  code: string;
  label: string;
  detail: string;
  source: string;
  source_id?: number | null;
  observed_at?: string | null;
  source_title?: string;
  work_date?: string | null;
  work_date_label?: string;
  source_url?: string;
  request_status?: string;
  request_at?: string | null;
  request_age_minutes?: number | null;
  run_status?: string;
  attention_horizon?: 'current_request' | 'earlier_follow_up';
  state_as_of?: string;
}

interface ActionStudent {
  student_id: number;
  student_name: string;
  priority: Priority;
  categories: string[];
  reasons: ActionReason[];
  next_action?: string;
  metrics: Record<string, number>;
  signal_fingerprint: string;
  workflow: {
    status: WorkflowStatus;
    note: string;
    snoozed_until?: string | null;
    evidence_changed: boolean;
  };
  contact: {
    recommended: boolean;
    recommended_channel: string;
    reason: string;
    contact_data_status: string;
    last_contact?: { method: string; status: string; occurred_at: string; outcome?: string } | null;
  };
  last_intervention?: { action_type: string; created_at: string } | null;
  steering_context?: {
    grade_level?: string;
    goal?: { id: number; title: string; target_metric_text: string; cycle_id: number; cycle_title: string; cycle_state: string } | null;
    milestones?: Array<{ id: number; expected_outcome: string; due_date: string; status: string; evidence_expected: boolean; linked_evidence_count: number; incomplete_evidence_count?: number }>;
    daily_priority?: { date?: string; priority: string; expected_outcome: string; result: string; next_move: string } | null;
    next_check_in?: { target_date: string; status: string; url: string } | null;
    cycle_end?: string;
    review_url?: string;
    workflow?: { mode: string; performance_review_expected: boolean };
    technical_coverage?: Record<string, { state: string }> | null;
    nsr_focus?: { primary?: { label: string; category: string } | null; secondary?: { label: string; category: string } | null } | null;
    latest_check_in?: { id: number; kind: string; agreed_next_deliverable: string; follow_up_on?: string | null; created_at: string } | null;
  };
}

interface CheckInDraft {
  barrier_problem: string;
  instructional_move: string;
  agreed_next_deliverable: string;
  follow_up_on: string;
  request_token: string;
}

interface QueueData {
  section_id: number;
  section_name: string;
  as_of_date: string;
  generated_at: string;
  students: ActionStudent[];
  counts: { total: number; urgent: number; today: number; monitor: number; contact_review: number };
  freshness: { latest_daily_summary_at?: string | null; note: string };
  contact_integration: { status: string; delivery_enabled: boolean };
  shared_instruction_groups: Array<{ detail: string; student_count: number }>;
}

interface AssistResult {
  summary?: string;
  recommended_action?: string;
  subject?: string;
  message?: string;
  talking_points?: string[];
  caution_note?: string;
  ai_status?: string;
  notes?: string;
}

interface DailyBriefProps {
  sectionId: number;
  targetDate: string;
}

const categoryLabels: Record<string, string> = {
  behind: 'Behind',
  makeup: 'Make-up',
  intervention: 'Intervention',
  clearer_instruction: 'Clearer instruction',
  parent_contact_review: 'Contact review',
};

const priorityClasses: Record<Priority, string> = {
  urgent: 'border-red-300 bg-red-50 text-red-900',
  today: 'border-amber-300 bg-amber-50 text-amber-900',
  monitor: 'border-blue-200 bg-blue-50 text-blue-900',
};

const priorityGroupMeta: Record<Priority, { title: string; description: string }> = {
  urgent: { title: 'High-priority follow-up', description: 'May include older unresolved work or contact needs; priority does not establish current activity.' },
  today: { title: 'Today', description: 'Action to complete during this class or school day.' },
  monitor: { title: 'Monitor', description: 'Keep visible; step in if the signal worsens.' },
};

const newRequestToken = () => (
  typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : ((value & 0x3) | 0x8)).toString(16);
    })
);

const emptyCheckInDraft = (): CheckInDraft => ({
  barrier_problem: '',
  instructional_move: '',
  agreed_next_deliverable: '',
  follow_up_on: '',
  request_token: newRequestToken(),
});

const DailyBrief: React.FC<DailyBriefProps> = ({ sectionId, targetDate }) => {
  const [data, setData] = useState<QueueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [busyStudentId, setBusyStudentId] = useState<number | null>(null);
  const [expandedStudentId, setExpandedStudentId] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [assists, setAssists] = useState<Record<number, { eventId: number; result: AssistResult }>>({});
  const [checkInDrafts, setCheckInDrafts] = useState<Record<number, CheckInDraft>>({});
  const requestVersion = useRef(0);

  const loadQueue = async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    try {
      const response = await studentActionQueueApi.get(sectionId, {
        date: targetDate,
        include_snoozed: showSnoozed,
      });
      if (version !== requestVersion.current) return;
      setData(response.data);
      const nextNotes: Record<number, string> = {};
      (response.data?.students || []).forEach((student: ActionStudent) => {
        nextNotes[student.student_id] = student.workflow.note || '';
      });
      setNotes(nextNotes);
    } catch (err: any) {
      if (version === requestVersion.current) setError(err.response?.data?.error || 'Failed to load the student action queue.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadQueue();
    return () => { requestVersion.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, targetDate, showSnoozed]);

  const updateWorkflow = async (student: ActionStudent, status: WorkflowStatus) => {
    setBusyStudentId(student.student_id);
    setError('');
    try {
      const payload: any = { status, note: notes[student.student_id] || '', date: targetDate };
      if (status === 'snoozed') {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        payload.snoozed_until = tomorrow.toISOString();
      }
      await studentActionQueueApi.updateWorkflow(sectionId, student.student_id, payload);
      await loadQueue();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not update this action.');
    } finally {
      setBusyStudentId(null);
    }
  };

  const requestAssist = async (student: ActionStudent, actionType: 'summarize' | 'suggest_intervention' | 'draft_family_email' | 'draft_phone_outline') => {
    setBusyStudentId(student.student_id);
    setError('');
    try {
      const response = await studentActionQueueApi.aiAssist(sectionId, student.student_id, {
        action_type: actionType,
        date: targetDate,
      });
      setAssists((current) => ({
        ...current,
        [student.student_id]: { eventId: response.data.event_id, result: response.data.assist },
      }));
      setExpandedStudentId(student.student_id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'AI assistance is unavailable right now.');
    } finally {
      setBusyStudentId(null);
    }
  };

  const logDraft = async (student: ActionStudent) => {
    const assist = assists[student.student_id];
    if (!assist) return;
    setBusyStudentId(student.student_id);
    try {
      await studentActionQueueApi.logContact(sectionId, student.student_id, {
        method: 'email',
        status: 'drafted',
        subject: assist.result.subject || 'Classroom progress check-in',
        message: assist.result.message || '',
      });
      await studentActionQueueApi.aiFeedback(assist.eventId, true);
      await loadQueue();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not save the contact draft.');
    } finally {
      setBusyStudentId(null);
    }
  };

  const updateCheckInDraft = (studentId: number, field: keyof CheckInDraft, value: string) => {
    setCheckInDrafts((current) => ({
      ...current,
      [studentId]: { ...(current[studentId] || emptyCheckInDraft()), [field]: value },
    }));
  };

  const recordCheckIn = async (student: ActionStudent) => {
    const goal = student.steering_context?.goal;
    const draft = checkInDrafts[student.student_id] || emptyCheckInDraft();
    if (!goal || !draft.agreed_next_deliverable.trim()) return;
    setBusyStudentId(student.student_id);
    setError('');
    try {
      const midpointDue = student.reasons.some((reason) => reason.code === 'cycle_midpoint_due');
      const senior = ['11th', '12th'].includes(student.steering_context?.grade_level || '');
      await studentActionQueueApi.createCheckIn(sectionId, student.student_id, {
        instructional_cycle: goal.cycle_id,
        check_in_kind: midpointDue ? 'midpoint' : 'instructional',
        signal_fingerprint: student.signal_fingerprint,
        request_token: draft.request_token,
        barrier_problem: draft.barrier_problem,
        instructional_move: draft.instructional_move,
        agreed_next_deliverable: draft.agreed_next_deliverable,
        follow_up_on: draft.follow_up_on || null,
        teacher_requested: senior,
        as_of_date: targetDate,
      });
      setCheckInDrafts((current) => ({ ...current, [student.student_id]: emptyCheckInDraft() }));
      await loadQueue();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Could not record this check-in. Refresh the queue and try again.');
    } finally {
      setBusyStudentId(null);
    }
  };

  const hasStudents = useMemo(() => Boolean(data?.students?.length), [data]);
  const priorityGroups = useMemo(() => (
    (['urgent', 'today', 'monitor'] as Priority[]).map((priority) => ({
      priority,
      ...priorityGroupMeta[priority],
      students: (data?.students || []).filter((student) => student.priority === priority),
    }))
  ), [data]);

  if (loading && !data) return <div className="p-8 text-center text-gray-500">Building today’s action queue…</div>;
  if (error && !data) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Teacher attention summary</h3>
            <p className="mt-1 text-sm text-gray-600">Current exceptions from the existing student action queue.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showSnoozed} onChange={(event) => setShowSnoozed(event.target.checked)} />
            Include snoozed
          </label>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            ['High-priority follow-up', data.counts.urgent, 'border-red-200 bg-red-50 text-red-900'],
            ['Action Today', data.counts.today, 'border-amber-200 bg-amber-50 text-amber-900'],
            ['Monitor', data.counts.monitor, 'border-blue-200 bg-blue-50 text-blue-900'],
          ].map(([label, value, classes]) => (
            <div key={String(label)} aria-label={`${label}: ${value}`} className={`rounded-lg border p-3 ${classes}`}>
              <div className="text-2xl font-semibold">{value}</div>
              <div className="text-xs font-medium uppercase tracking-wide">{label}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">Generated {new Date(data.generated_at).toLocaleString()}. {data.freshness.note}</p>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {!hasStudents && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-green-900">You’re clear right now.</p>
          <p className="mt-1 text-sm text-green-700">No students currently meet the action-queue rules. Continue instruction and check back as new signals arrive.</p>
          <p className="mt-2 text-xs text-green-700">No actionable signals does not mean every source has complete data.</p>
        </div>
      )}

      {priorityGroups.map((group) => group.students.length > 0 ? (
        <section key={group.priority} aria-labelledby={`priority-${group.priority}`} className="space-y-3">
          <div className="flex items-end justify-between gap-3 border-b border-gray-200 pb-2">
            <div>
              <h3 id={`priority-${group.priority}`} className="text-base font-semibold text-gray-900">{group.title}</h3>
              <p className="text-xs text-gray-600">{group.description}</p>
            </div>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">{group.students.length}</span>
          </div>
          <div className="space-y-3">
          {group.students.map((student) => {
        const expanded = expandedStudentId === student.student_id;
        const expandedRegionId = `daily-brief-student-${student.student_id}-details`;
        const assist = assists[student.student_id];
        const busy = busyStudentId === student.student_id;
        const checkInDraft = checkInDrafts[student.student_id] || emptyCheckInDraft();
        const context = student.steering_context;
        return (
          <article key={student.student_id} className={`rounded-lg border p-4 ${priorityClasses[student.priority]}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-semibold">{student.student_name}</h4>
                  <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-semibold uppercase">{student.priority}</span>
                  {student.workflow.status !== 'open' && (
                    <span className="rounded-full border border-current/20 px-2 py-0.5 text-xs">{student.workflow.status.replace('_', ' ')}</span>
                  )}
                  {student.workflow.evidence_changed && <span className="text-xs font-medium">New evidence</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {student.categories.map((category) => (
                    <span key={category} className="rounded-full border border-current/20 bg-white/60 px-2 py-0.5 text-xs">
                      {categoryLabels[category] || category.split('_').join(' ')}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="text-sm font-medium underline"
                aria-expanded={expanded}
                aria-controls={expandedRegionId}
                onClick={() => setExpandedStudentId(expanded ? null : student.student_id)}
              >
                {expanded ? 'Hide details' : 'Review and act'}
              </button>
            </div>

            <div className="mt-3 space-y-1 text-sm">
              {student.next_action && <p className="mb-2 font-semibold">Next action: {student.next_action}</p>}
              {student.reasons.slice(0, expanded ? undefined : 2).map((reason, index) => (
                <div className="pb-2" key={`${reason.code}-${reason.source_id || index}`}>
                  <div><span className="font-medium">{reason.label}:</span> {reason.detail}</div>
                  {reason.source_title && <div className="mt-1 text-xs">Source: {reason.source_title}{reason.work_date && ` · ${reason.work_date_label || 'Work date'}: ${reason.work_date}`}</div>}
                  {reason.request_status && <div className="mt-2 rounded border border-current/20 p-2 text-sm">
                    <p className="font-semibold">{reason.attention_horizon === 'current_request' ? 'Open help in active lesson' : 'Open help from an inactive lesson — follow up'}</p>
                    <p>Request: {reason.request_status} · Lesson: {reason.run_status}</p>
                    <p>{reason.request_at ? `Help requested: ${new Date(reason.request_at).toLocaleString()}` : 'Request time unavailable'}{reason.request_age_minutes != null && ` · ${reason.request_age_minutes} minutes before refresh`}</p>
                    <p className="text-xs">Current saved state at {reason.state_as_of ? new Date(reason.state_as_of).toLocaleString() : 'refresh'}, not a historical snapshot or proof the student is online.</p>
                  </div>}
                  <div className="mt-1 text-xs">
                    {reason.work_date
                      ? reason.work_date < data.as_of_date ? 'Earlier work — check follow-up state'
                        : reason.work_date === data.as_of_date ? 'Work dated to this brief'
                          : 'Upcoming dated work'
                      : 'Work date unavailable — check source freshness'}
                    {reason.observed_at && ` · Signal recorded: ${new Date(reason.observed_at).toLocaleString()}`}
                  </div>
                  {reason.source_url && <Link className="text-xs font-medium underline" to={reason.source_url}>{reason.source === 'daily_completion' ? 'View work from this day' : 'Open source work'}</Link>}
                </div>
              ))}
              {!expanded && student.reasons.length > 2 && <p className="text-xs">{student.reasons.length - 2} more signals in Review and act.</p>}
              {context?.daily_priority?.next_move && (
                <div className="mt-2 rounded-md border border-current/15 bg-white/70 px-3 py-2">
                  <span className="font-semibold">Next move:</span> {context.daily_priority.next_move}
                </div>
              )}
            </div>

            {expanded && (
              <div id={expandedRegionId} className="mt-4 space-y-4 border-t border-current/10 pt-4">
                {context?.goal && (
                  <div className="rounded-md border border-current/20 bg-white/80 p-3 text-sm text-gray-800">
                    <div className="font-semibold">{context.goal.title}</div>
                    <p className="mt-1 text-xs text-gray-600">{context.goal.cycle_title} · {context.goal.target_metric_text}</p>
                    {(context.milestones || []).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {(context.milestones || []).map((milestone) => (
                          <p key={milestone.id} className="text-xs">
                            <span className="font-medium">{milestone.status.split('_').join(' ')}:</span> {milestone.expected_outcome}
                            {` · Due ${milestone.due_date}`}
                            {milestone.evidence_expected ? ` · ${milestone.linked_evidence_count ? `${milestone.linked_evidence_count} evidence item(s) linked` : 'no evidence linked yet'}` : ''}
                            {!!milestone.incomplete_evidence_count && ` · ${milestone.incomplete_evidence_count} unfinished`}
                          </p>
                        ))}
                      </div>
                    )}
                    {context.daily_priority && <p className="mt-2 text-xs"><span className="font-medium">Daily work{context.daily_priority.date ? ` · ${context.daily_priority.date}` : ''}:</span> {context.daily_priority.priority || 'No priority recorded'} {context.daily_priority.result ? `· ${context.daily_priority.result.split('_').join(' ')}` : ''}</p>}
                    {context.technical_coverage && <p className="mt-2 text-xs"><span className="font-medium">Evidence coverage (work and assessments):</span> {Object.entries(context.technical_coverage).map(([stage, value]) => `${stage.toUpperCase()} ${value.state === 'not_assessed' ? 'no evidence linked' : value.state.split('_').join(' ')}`).join(' · ')}. Verification is not a proficiency grade.</p>}
                    {context.next_check_in && <p className="mt-2 text-xs"><Link className="underline" to={context.next_check_in.url}>Next check-in: {context.next_check_in.target_date}</Link></p>}
                    {context.workflow?.performance_review_expected && context.review_url && <p className="mt-2 text-xs"><Link className="underline" to={context.review_url}>Performance review · cycle ends {context.cycle_end}</Link></p>}
                    {context.nsr_focus?.primary && <p className="mt-2 text-xs"><span className="font-medium">NSR focus:</span> {context.nsr_focus.primary.category} · {context.nsr_focus.primary.label}</p>}
                    {context.latest_check_in && <p className="mt-2 text-xs"><span className="font-medium">Last agreed deliverable:</span> {context.latest_check_in.agreed_next_deliverable}</p>}
                  </div>
                )}
                {context?.goal && (
                  <div className="rounded-md border border-current/20 bg-white/80 p-3 text-gray-900">
                    <div className="text-xs font-semibold uppercase tracking-wide">Record brief check-in</div>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      <label className="text-xs font-medium">Barrier/problem
                        <textarea className="mt-1 w-full rounded border border-gray-300 p-2 font-normal" rows={2} maxLength={500} value={checkInDraft.barrier_problem} onChange={(event) => updateCheckInDraft(student.student_id, 'barrier_problem', event.target.value)} />
                      </label>
                      <label className="text-xs font-medium">Teacher instructional move
                        <textarea className="mt-1 w-full rounded border border-gray-300 p-2 font-normal" rows={2} maxLength={500} value={checkInDraft.instructional_move} onChange={(event) => updateCheckInDraft(student.student_id, 'instructional_move', event.target.value)} placeholder="For example: model, clarify, reteach, or pull into live help" />
                      </label>
                      <label className="text-xs font-medium">Agreed next deliverable
                        <textarea className="mt-1 w-full rounded border border-gray-300 p-2 font-normal" rows={2} maxLength={500} value={checkInDraft.agreed_next_deliverable} onChange={(event) => updateCheckInDraft(student.student_id, 'agreed_next_deliverable', event.target.value)} />
                      </label>
                      <label className="text-xs font-medium">Optional follow-up
                        <input type="date" className="mt-1 block w-full rounded border border-gray-300 p-2 font-normal" value={checkInDraft.follow_up_on} onChange={(event) => updateCheckInDraft(student.student_id, 'follow_up_on', event.target.value)} />
                      </label>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">This agreed record is visible to the student. Use private teacher comments for private context.</p>
                    <button disabled={busy || !checkInDraft.agreed_next_deliverable.trim()} className="mt-2 rounded bg-indigo-700 px-3 py-2 text-xs font-medium text-white disabled:opacity-50" onClick={() => void recordCheckIn(student)}>Save check-in</button>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide">Teacher note</label>
                  <textarea
                    className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900"
                    rows={2}
                    value={notes[student.student_id] || ''}
                    onChange={(event) => setNotes((current) => ({ ...current, [student.student_id]: event.target.value }))}
                    placeholder="Barrier, agreed next step, or follow-up context"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link className="rounded border border-current/30 bg-white px-3 py-2 text-xs font-medium" to={`/v2/teacher/students/${student.student_id}/work?section_id=${sectionId}`}>Open work history</Link>
                  {student.categories.includes('makeup') && <Link className="rounded border border-current/30 bg-white px-3 py-2 text-xs font-medium" to="/v2/teacher/queue">Open Make-up Queue</Link>}
                  <button disabled={busy} className="rounded bg-slate-800 px-3 py-2 text-xs font-medium text-white disabled:opacity-50" onClick={() => updateWorkflow(student, 'in_progress')}>Start follow-up</button>
                  <button disabled={busy} className="rounded border border-current/30 bg-white px-3 py-2 text-xs font-medium disabled:opacity-50" onClick={() => updateWorkflow(student, 'snoozed')}>Snooze 1 day</button>
                  <button disabled={busy} className="rounded border border-green-300 bg-green-100 px-3 py-2 text-xs font-medium text-green-900 disabled:opacity-50" onClick={() => updateWorkflow(student, 'resolved')}>Resolve</button>
                  <button disabled={busy} className="rounded border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50" onClick={() => updateWorkflow(student, 'dismissed')}>Dismiss signal</button>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide">Teacher-controlled AI assistance</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button disabled={busy} className="rounded border border-violet-300 bg-violet-100 px-3 py-2 text-xs font-medium text-violet-900 disabled:opacity-50" onClick={() => requestAssist(student, 'summarize')}>Summarize evidence</button>
                    <button disabled={busy} className="rounded border border-violet-300 bg-violet-100 px-3 py-2 text-xs font-medium text-violet-900 disabled:opacity-50" onClick={() => requestAssist(student, 'suggest_intervention')}>Suggest intervention</button>
                    {student.contact.recommended && (
                      <>
                        <button disabled={busy} className="rounded border border-violet-300 bg-violet-100 px-3 py-2 text-xs font-medium text-violet-900 disabled:opacity-50" onClick={() => requestAssist(student, 'draft_family_email')}>Draft family email</button>
                        <button disabled={busy} className="rounded border border-violet-300 bg-violet-100 px-3 py-2 text-xs font-medium text-violet-900 disabled:opacity-50" onClick={() => requestAssist(student, 'draft_phone_outline')}>Draft phone outline</button>
                      </>
                    )}
                  </div>
                </div>
                {assist && (
                  <div className="rounded-md border border-violet-200 bg-white p-3 text-sm text-gray-800">
                    {assist.result.summary && <p><span className="font-medium">Summary:</span> {assist.result.summary}</p>}
                    {assist.result.recommended_action && <p className="mt-2"><span className="font-medium">Suggested move:</span> {assist.result.recommended_action}</p>}
                    {assist.result.subject && <p className="mt-2"><span className="font-medium">Subject:</span> {assist.result.subject}</p>}
                    {assist.result.message && <p className="mt-2 whitespace-pre-wrap">{assist.result.message}</p>}
                    {assist.result.talking_points && assist.result.talking_points.length > 0 && (
                      <ul className="mt-2 list-disc pl-5">{assist.result.talking_points.map((point) => <li key={point}>{point}</li>)}</ul>
                    )}
                    <p className="mt-2 text-xs text-gray-500">{assist.result.caution_note} {assist.result.ai_status === 'fallback' ? 'Fallback wording shown.' : ''}</p>
                    {assist.result.message && (
                      <button disabled={busy} className="mt-3 rounded bg-violet-700 px-3 py-2 text-xs font-medium text-white disabled:opacity-50" onClick={() => logDraft(student)}>
                        Save as draft only
                      </button>
                    )}
                  </div>
                )}
                {student.contact.recommended && (
                  <div className="rounded-md border border-gray-200 bg-white/80 p-3 text-sm text-gray-700">
                    <div className="font-medium">Family contact review</div>
                    <p className="mt-1">{student.contact.reason}</p>
                    <p className="mt-1 text-xs text-gray-500">Skyward contact integration is not configured. Elevate can save drafts and manual logs, but cannot find recipients or send messages.</p>
                    {student.contact.last_contact && <p className="mt-2 text-xs">Last logged contact: {student.contact.last_contact.method}, {new Date(student.contact.last_contact.occurred_at).toLocaleString()}</p>}
                  </div>
                )}
              </div>
            )}
          </article>
        );
          })}
          </div>
        </section>
      ) : null)}

      {data.shared_instruction_groups.length > 0 && (
        <details className="rounded-lg border border-violet-200 bg-violet-50 p-4">
          <summary className="cursor-pointer font-medium text-violet-900">Shared reteach suggestions</summary>
          <p className="mt-1 text-xs text-violet-700">Secondary planning support after individual attention needs are reviewed.</p>
          {data.shared_instruction_groups.map((group, index) => (
            <div key={`${group.detail}-${index}`} className="mt-2 text-sm text-violet-800">{group.student_count} students: {group.detail}</div>
          ))}
        </details>
      )}
    </div>
  );
};

export default DailyBrief;
